import {
  accessRightsShape,
  ActionBar,
  ActionBarButtons,
  cardPaymentDetailsShape,
  Clue,
  Column,
  Container,
  convertBytesToGB,
  Details,
  DetailsItem,
  EditActionButton,
  formatDate,
  formatNumber,
  invoicePaymentDetailsShape,
  isNotEmpty,
  List,
  Loader,
  ProgressBar,
  Row,
  url,
  useAccessRightsQuery,
  useApi,
  useBackend,
  useFlipperQuery,
  useNotifier,
} from "lcm-iot-commons";
import { useEffect, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import SubscriptionBillingDetails from "lcm-iot-commons/client/lib/components/Subscriptions/SubscriptionBillingDetails";
import SubscriptionAssignedAssetsList from "lcm-iot-commons/client/lib/components/Subscriptions/SubscriptionAssignedAssetsList";
import {
  extractConnectSubscription,
  extractCurrentSubscription,
} from "../../extractors/connectSubscriptionsExtractor";
import { loadClientApplicationAccessRights } from "../../api";
import OpcUaServerItem from "../OpcUaServer/OpcUaServerItem";
import OpcUaServerHelp from "../OpcUaServer/OpcUaServerHelp";
import ApiKeys from "./ApiKeys";
import ConnectSubscriptionPaymentsSection from "./ConnectSubscriptionPaymentsSection";
import TechnicalUsers from "../TechnicalUsers/TechnicalUsers";
import ConnectSubscriptionEdgeDevices from "./ConnectSubscriptionEdgeDevices";
import {
  opcUaServerShape,
  opcUaServersShape,
} from "../../shapes/opcUaServersShape";
import {
  clientApplicationsResponseShape,
  clientApplicationsShape,
} from "../../shapes/clientApplicationsShape";
import {
  connectSubscriptionAddOnShape,
  connectSubscriptionShape,
} from "../../shapes/connectSubscriptionsShape";

function useSubscriptionDetails(id: string) {
  const api = useApi();
  const backend = useBackend();
  const { data: flipper } = useFlipperQuery();
  const { data: accessRights } = useAccessRightsQuery("APISubscription", {
    enabled: !!id,
  });
  const {
    data: subscriptionData,
    isLoading: isLoadingSubscriptions,
    refetch: refetchSubscriptionData,
  } = api.get.useQuery(`/api_subscriptions/${id}`, null, {
    enabled: !!id,
    select: extractConnectSubscription,
  });
  const { data: clientApplication, isFetching: isFetchingClientApplication } =
    api.get.useQuery(
      `/client_applications/${subscriptionData?.clientApplicationId}`,
      {
        include: "technical_users",
      },
      {
        enabled: !!subscriptionData?.clientApplicationId,
        select: (response: clientApplicationsResponseShape) =>
          ({
            id: response.id,
            name: response.name,
            description: response.description,
            redirectURIs: response.redirect_uris?.join("\n") || "",
            technicalUsers: response.technical_users,
          }) as clientApplicationsShape,
      },
    );
  const {
    data: subscriptionFromBackend,
    isFetching: isFetchingSubscriptionFromBackend,
    refetch: refetchSubscriptionFromBackend,
  } = backend.get.useQuery(
    `/subscriptions/${id}`,
    { include: "payment_details" },
    {
      enabled: !!id && (accessRights as accessRightsShape)?.canUpdate,
      select: extractCurrentSubscription,
    },
  );
  const { data: opcUaServerData } = backend.get.useQuery(
    `/api_subscriptions/${subscriptionData?.id}/opc_ua_servers`,
    null,
    {
      enabled:
        !!subscriptionData?.id &&
        !!subscriptionFromBackend?.addons?.some(
          (addon: connectSubscriptionAddOnShape) =>
            addon?.id?.includes("opc-ua-server"),
        ),
      select: (response: opcUaServersShape) => {
        const server = response.opc_ua_servers || [];

        if (!server.length) return null;

        const {
          id: serverId,
          status,
          hostname,
          subscription_id: subscriptionId,
        } = server[0];
        return {
          id: serverId,
          status,
          hostname,
          subscription_id: subscriptionId,
        } as opcUaServerShape;
      },
    },
  );
  const { mutateAsync: updatePaymentToInvoice, isLoading: isUpdatingPayment } =
    backend.patch.useMutation(`/api_subscriptions/${id}/payment/to_invoice`);

  const isLoading =
    isFetchingClientApplication ||
    isFetchingSubscriptionFromBackend ||
    isLoadingSubscriptions ||
    isUpdatingPayment;

  return {
    accessRights: accessRights as accessRightsShape,
    isLoading,
    subscriptionData: subscriptionData as connectSubscriptionShape | null,
    clientApplication,
    subscriptionFromBackend,
    opcUaServerData,
    assetAssignmentEnabled: flipper?.assetAssignmentActive,
    refetchSubscriptionData,
    refetchSubscriptionFromBackend,
    updatePaymentToInvoice,
  };
}

function SubscriptionStatus({
  subscriptionData,
  invoiceDetailsList,
  intl,
}: Readonly<{
  subscriptionData: connectSubscriptionShape | null;
  invoiceDetailsList: invoicePaymentDetailsShape | null;
  intl: IntlShape;
}>) {
  function getSubscriptionStatusText() {
    switch (subscriptionData?.status) {
      case "confirmed":
      case "scheduled_update":
        return intl.formatMessage({ id: "subscription_details.active" });
      case "non_renewing":
      case "cancelled":
        return intl.formatMessage({ id: "subscription_details.cancelled" });
      case "payment_required":
        return intl.formatMessage({
          id: "subscription_details.payment_required",
        });
      case "open":
        return intl.formatMessage({ id: "subscription_details.open" });
      default:
        return intl.formatMessage({ id: "subscription_details.active" });
    }
  }
  function getSubscriptionEndingOrRenewingOrPaymentRequired() {
    switch (subscriptionData?.status) {
      case "payment_required":
        if (
          invoiceDetailsList &&
          invoiceDetailsList.filter(
            (i) =>
              i?.status &&
              (i.status === "posted" || i.status === "payment_due"),
          ).length > 0
        ) {
          const invoices = invoiceDetailsList
            .filter(
              (invoiceDetails) =>
                invoiceDetails.status === "posted" ||
                invoiceDetails.status === "payment_due",
            )
            .map((invoiceDetails, index) => {
              const invoice =
                `${intl.formatMessage({
                  id: "subscription_details.amount_label",
                })} ` +
                `${intl.formatNumber(
                  parseInt(invoiceDetails.amountDue ?? "0", 10),
                  {
                    minimumFractionDigits: 2,
                  },
                )} ${invoiceDetails.currency} ` +
                `${intl.formatMessage({
                  id: "subscription_details.due_by",
                })} ${formatDate(invoiceDetails.dueDate, intl)}`;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{invoice}</li>
              );
            });
          return <ul>{invoices}</ul>;
        }
        return `(${intl.formatMessage({ id: "subscription_details.renewing_on" })} ${formatDate(subscriptionData?.billedUntil, intl)})`;
      case "confirmed":
      case "scheduled_update":
      case "open":
        return `(${intl.formatMessage({ id: "subscription_details.renewing_on" })} ${formatDate(subscriptionData?.billedUntil, intl)})`;
      case "non_renewing":
      case "cancelled":
        return `(${intl.formatMessage({ id: "subscription_details.ending_on" })} ${formatDate(subscriptionData?.billedUntil, intl)})`;
      default:
        return `(${intl.formatMessage({ id: "subscription_details.renewing_on" })} ${formatDate(subscriptionData?.billedUntil, intl)})`;
    }
  }
  return (
    <>
      <div>{getSubscriptionStatusText()}</div>
      {getSubscriptionEndingOrRenewingOrPaymentRequired()}
    </>
  );
}

function OpcUaServerSection({
  opcUaServerData,
}: {
  readonly opcUaServerData: opcUaServerShape | null;
}) {
  function renderOpcUaServerList() {
    return opcUaServerData ? (
      <List id="opc-ua-server-item-list">
        <OpcUaServerItem opcUaServer={opcUaServerData} />
      </List>
    ) : (
      <div id="no-opc-ua-server-found">
        <FormattedMessage id="subscription_details.no_opc_ua_server_found" />
      </div>
    );
  }

  return (
    <div id="subscription-opc-ua-server" className="row space-before">
      <div className="col-md-12">
        <ActionBar>
          <h2>
            <FormattedMessage id="subscription_details.opc_ua_server.header" />
            <OpcUaServerHelp />
          </h2>
        </ActionBar>
        {renderOpcUaServerList()}
      </div>
    </div>
  );
}

function SubscriptionPlanDetails({
  clientApplication,
  subscriptionData,
  subscriptionFromBackend,
  accessRights,
  intl,
}: Readonly<{
  clientApplication: clientApplicationsShape | undefined;
  subscriptionData: connectSubscriptionShape | null;
  subscriptionFromBackend:
    | ReturnType<typeof extractCurrentSubscription>
    | undefined;
  accessRights: accessRightsShape | null;
  intl: IntlShape;
}>) {
  const isPaid = isNotEmpty(subscriptionData?.externalReference);
  const connectedAssetAddonQuantity = subscriptionFromBackend?.addons?.filter(
    (addon: connectSubscriptionAddOnShape) =>
      addon?.id?.includes("connect-connected-asset"),
  )[0]?.quantity;

  const convertToPercent = (total: number = 0, value: number = 0) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };
  const convertToGB = (bytes?: number) => {
    if (!bytes) return "0";
    return formatNumber(convertBytesToGB(bytes));
  };

  const connectedAssetsInPercent = convertToPercent(
    connectedAssetAddonQuantity,
    subscriptionData?.connectedAssetsUsed,
  );
  const apiCallsInPercent = convertToPercent(
    subscriptionData?.apiCallQuota,
    subscriptionData?.apiCallsUsed,
  );
  const uploadDownloadInPercent = convertToPercent(
    subscriptionData?.uploadDownloadQuota,
    subscriptionData?.uploadDownloadUsed,
  );
  const storageInPercent = convertToPercent(
    subscriptionData?.storageQuota,
    subscriptionData?.storageUsed,
  );

  const subscriptionStorageQuotaInGB = convertToGB(
    subscriptionData?.storageQuota,
  );
  const subscriptionStorageUsedInGB = convertToGB(
    subscriptionData?.storageUsed,
  );
  const subscriptionUploadDownloadQuotaInGB = convertToGB(
    subscriptionData?.uploadDownloadQuota,
  );
  const subscriptionUploadDownloadUsedInGB = convertToGB(
    subscriptionData?.uploadDownloadUsed,
  );

  const subscriptionApiCallsQuota = subscriptionData?.apiCallQuota
    ? formatNumber(subscriptionData.apiCallQuota)
    : "0";
  const subscriptionApiCallsUsed = subscriptionData?.apiCallsUsed
    ? formatNumber(subscriptionData.apiCallsUsed)
    : "0";
  const redirectUris = clientApplication?.redirectURIs?.length
    ? clientApplication?.redirectURIs.split(/\n/)
    : [];
  const displayUris = redirectUris.length ? (
    <ul id="redirectUri-list" className="address">
      {redirectUris.map((uri: string) => (
        <li key={uri}>{uri}</li>
      ))}
    </ul>
  ) : (
    <div className="details-item-value">
      <span>-</span>
    </div>
  );
  const canUpgrade =
    subscriptionData?.status !== "non_renewing" &&
    subscriptionData?.status !== "cancelled" &&
    accessRights?.canUpdate;
  return (
    <Row id="subscription-plan-details">
      <Column>
        <Details>
          <DetailsItem
            id="subscription-client-application-name"
            translationKey="label.client_application"
            value={clientApplication?.name}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; translationKey: string; value:...
            intl={intl}
          />
          <DetailsItem
            id="subscription-client-application-description"
            translationKey="label.description"
            value={clientApplication?.description}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; translationKey: string; value:...
            intl={intl}
          />
          <div
            id="subscription-client-application-redirect-uris"
            className="details-item"
          >
            <div className="details-item-name">
              {intl.formatMessage({ id: "label.redirect_uris" })}
            </div>
            {displayUris}
          </div>
          <DetailsItem
            id="subscription-plan-name"
            translationKey="subscription_details.current_plan"
            value={subscriptionData?.subscriptionName}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; translationKey: string; value:...
            intl={intl}
          />
          {isPaid && (
            <DetailsItem
              id="subscription-status"
              translationKey="subscription_details.status"
              // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; translation...
              intl={intl}
            >
              <SubscriptionStatus
                subscriptionData={subscriptionData}
                invoiceDetailsList={
                  subscriptionFromBackend?.invoicePaymentDetails || null
                }
                intl={intl}
              />
            </DetailsItem>
          )}
          <DetailsItem
            id="subscription-upload-download-quota"
            translationKey="subscription_details.upload_download"
            // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; translation...
            intl={intl}
          >
            <div id="subscription-upload-download-progressbar">
              <span>
                {`${subscriptionUploadDownloadUsedInGB} GB / ${subscriptionUploadDownloadQuotaInGB} GB`}
              </span>
              <ProgressBar percent={uploadDownloadInPercent} />
            </div>
          </DetailsItem>
          <DetailsItem
            id="subscription-storage-quota"
            translationKey="subscription_details.storage"
            // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; translation...
            intl={intl}
          >
            <div id="subscription-storage-progressbar">
              <span>
                {`${subscriptionStorageUsedInGB} GB / ${subscriptionStorageQuotaInGB} GB`}
              </span>
              <ProgressBar percent={storageInPercent} />
            </div>
          </DetailsItem>
          <DetailsItem
            id="subscription-apicalls-quota"
            translationKey="subscription_details.api_calls"
            // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; translation...
            intl={intl}
          >
            <div id="subscription-apicalls-progressbar">
              <span>
                {`${subscriptionApiCallsUsed} / ${subscriptionApiCallsQuota}`}
              </span>
              <ProgressBar percent={apiCallsInPercent} />
            </div>
          </DetailsItem>
          {connectedAssetAddonQuantity ? (
            <DetailsItem
              id="subscription-connected-assets-fraction"
              translationKey="subscription_details.connected_assets"
              // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; translation...
              intl={intl}
            >
              <div id="subscription-connected-assets-progressbar">
                <span>
                  {`${formatNumber(subscriptionData?.connectedAssetsUsed)} / ${formatNumber(connectedAssetAddonQuantity)}`}
                </span>
                <ProgressBar percent={connectedAssetsInPercent} />
              </div>
            </DetailsItem>
          ) : null}
        </Details>
        <div className="btn-group">
          {canUpgrade ? (
            <Link
              id="upgrade-button"
              className="btn btn-primary"
              to={url(`/subscriptions/connect/${subscriptionData?.id}/upgrade`)}
            >
              <FormattedMessage id="subscription_details.upgrade_button" />
            </Link>
          ) : null}
        </div>
      </Column>
    </Row>
  );
}

function SubscriptionBillingSection({
  accessRights,
  subscriptionData,
  editDisabled,
  perInvoice,
}: Readonly<{
  subscriptionData: connectSubscriptionShape | null;
  accessRights: accessRightsShape | null;
  editDisabled: boolean;
  perInvoice: boolean;
}>) {
  if (!subscriptionData) return null;
  const isPaid = isNotEmpty(subscriptionData?.externalReference);
  if (!(isPaid && accessRights?.canUpdate)) return null;
  return (
    <>
      <Row id="subscription-billing-details">
        <Column>
          <ActionBar>
            <h2>
              <FormattedMessage id="subscription_details.billing_details_header" />
            </h2>
            <ActionBarButtons>
              <EditActionButton
                id="edit-billing-details-button"
                target={`/subscriptions/connect/${subscriptionData.id}/billing_details/edit`}
                disabled={
                  editDisabled ||
                  (perInvoice && subscriptionData?.status !== "confirmed")
                }
              />
            </ActionBarButtons>
          </ActionBar>
        </Column>
      </Row>
      <SubscriptionBillingDetails
        // @ts-expect-error TS(2322) FIXME: Type '{ billingName: string | undefined; billingEma... Remove this comment to see the full error message
        billingAddress={subscriptionData?.billingAddress}
        shippingAddress={subscriptionData?.shippingAddress}
        customerPurchaseOrder={subscriptionData?.customerPurchaseOrder}
      />
    </>
  );
}

export default function ConnectSubscriptionDetails() {
  const intl = useIntl();
  const notifier = useNotifier();
  const { id } = useParams<{ id: string }>();

  const [paymentRequired, setPaymentRequired] = useState(false);
  const [clientApplicationAccessRights, setClientApplicationAccessRights] =
    useState<accessRightsShape>();
  const [opcUaServerAddon, setOpcUaServerAddon] = useState<boolean>(false);

  const {
    accessRights,
    isLoading,
    subscriptionData,
    clientApplication,
    subscriptionFromBackend,
    opcUaServerData,
    assetAssignmentEnabled,
    refetchSubscriptionData,
    refetchSubscriptionFromBackend,
    updatePaymentToInvoice,
  } = useSubscriptionDetails(id);

  const editDisabled = isLoading || !accessRights?.canUpdate;
  const perInvoice = subscriptionFromBackend?.invoicePaymentDetails?.length > 0;

  useEffect(() => {
    if (subscriptionData && !clientApplicationAccessRights) {
      loadClientApplicationAccessRights(
        subscriptionData?.clientApplicationId,
      ).then((response) => {
        setClientApplicationAccessRights(response);
      });
      setPaymentRequired(subscriptionData?.status === "payment_required");
    }
    if (
      subscriptionFromBackend?.addons?.some(
        (addon: connectSubscriptionAddOnShape) =>
          addon?.id?.includes("opc-ua-server"),
      )
    ) {
      setOpcUaServerAddon(true);
    }
  }, [subscriptionData, subscriptionFromBackend]);

  const handleOnPaymentMethodChange = async () => {
    await updatePaymentToInvoice();
    Promise.all([
      refetchSubscriptionData(),
      refetchSubscriptionFromBackend(),
    ]).then(() => {
      notifier.showSuccess(
        intl.formatMessage({
          id: "payment_details_edit.change_to_invoice.success_message",
        }),
      );
    });
  };

  const paymentRequiredClue =
    accessRights?.canUpdate && paymentRequired ? (
      // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; title: stri...
      <Clue
        id="payment-required-notification"
        title={intl.formatMessage({
          id: "subscription.payment_required.title",
        })}
        className="notification"
        highlighted
      >
        <div className="clue-details">
          <FormattedMessage id="subscription.payment_required.details-1" />
          <br />
          <FormattedMessage id="subscription.payment_required.details-2" />
        </div>
      </Clue>
    ) : null;

  const assignedAssetList =
    subscriptionData && assetAssignmentEnabled ? (
      <>
        <Row id="subscription-assigned-assets-list">
          <Column>
            <ActionBar>
              <h2 id="asset_list_header">
                <FormattedMessage id="subscription_details.asset_list_edit_header" />
                {subscriptionData.numberAssignedAssets > 0
                  ? ` (${formatNumber(subscriptionData.numberAssignedAssets)})`
                  : null}
              </h2>
              <ActionBarButtons>
                <EditActionButton
                  id="asset_list_edit_button"
                  target={`/subscriptions/connect/${id}/assets/assign`}
                />
              </ActionBarButtons>
            </ActionBar>
          </Column>
        </Row>
        <SubscriptionAssignedAssetsList
          // @ts-expect-error TS(2322) FIXME: Type '{ subscriptionId: any; subscriptionRoute: st... Remove this comment to see the full error message
          subscriptionId={id}
          subscriptionRoute="api_subscriptions"
        />
      </>
    ) : null;

  const canChangeToInvoice = ["confirmed", "scheduled_update", "open"].includes(
    subscriptionData?.status ?? "",
  );
  return (
    <Container id="subscription-details">
      {paymentRequiredClue}
      <Row>
        <ActionBar>
          <h1>
            <FormattedMessage id="subscription_details.header" />
          </h1>
          <ActionBarButtons>
            <EditActionButton
              id="edit-client-application-button"
              target={`/subscriptions/connect/${id}/edit`}
              disabled={
                isLoading ||
                !(clientApplicationAccessRights as accessRightsShape)?.canUpdate
              }
            />
          </ActionBarButtons>
        </ActionBar>
      </Row>
      <Row>
        <Loader id="connect-subscription-details-loader" loading={isLoading} />
        {!isLoading && (
          <>
            <SubscriptionPlanDetails
              clientApplication={clientApplication}
              subscriptionData={subscriptionData}
              subscriptionFromBackend={subscriptionFromBackend}
              accessRights={accessRights}
              intl={intl}
            />
            <ConnectSubscriptionEdgeDevices
              canUpdateClientApplication={
                clientApplicationAccessRights?.canUpdate
              }
            />
            {(opcUaServerAddon || opcUaServerData) && (
              <OpcUaServerSection opcUaServerData={opcUaServerData ?? null} />
            )}
            <ApiKeys
              canUpdate={clientApplicationAccessRights?.canUpdate}
              clientApplicationId={clientApplication?.id}
            />
            <TechnicalUsers
              canUpdateClientApplication={
                clientApplicationAccessRights?.canUpdate
              }
              clientApplicationId={clientApplication?.id}
              technicalUsers={clientApplication?.technicalUsers}
            />
            <SubscriptionBillingSection
              accessRights={accessRights}
              subscriptionData={subscriptionData}
              editDisabled={editDisabled}
              perInvoice={perInvoice}
            />
            {accessRights?.canUpdate &&
              subscriptionFromBackend &&
              subscriptionData?.externalReference && (
                <ConnectSubscriptionPaymentsSection
                  disabledEdit={
                    editDisabled ||
                    (perInvoice && subscriptionData?.status !== "confirmed")
                  }
                  cardPaymentDetails={
                    subscriptionFromBackend?.cardPaymentDetails as cardPaymentDetailsShape
                  }
                  perInvoice={perInvoice}
                  invoicePaymentDetails={
                    subscriptionFromBackend?.invoicePaymentDetails
                  }
                  canChangeToInvoice={canChangeToInvoice}
                  onPaymentMethodChange={handleOnPaymentMethodChange}
                />
              )}
            {assignedAssetList}
          </>
        )}
      </Row>
    </Container>
  );
}
